/**
 * AppAuth client for Dailey Core integration
 * Based on the Dailey Core Integration Guide
 * Includes WebAuthn (passkey) support
 */

import { startRegistration, startAuthentication } from '@simplewebauthn/browser';
import { clearPrivateCacheScope, updatePrivateCacheTenant } from './privateCacheScope.js';

function sanitizeUrl(value) {
  if (!value) return '';
  const trimmed = String(value).trim();
  if (!trimmed) return '';
  const first = trimmed.split(/\s+/)[0];
  return first.replace(/\/$/, '');
}

function parseJwtPayload(token) {
  try {
    const payload = token?.split('.')?.[1];
    if (!payload) return null;
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

function isPrivateHost(hostname) {
  return /^(localhost|127\.|0\.0\.0\.0)$/.test(hostname)
    || /^10\./.test(hostname)
    || /^192\.168\./.test(hostname)
    || /^172\.(1[6-9]|2\d|3[0-1])\./.test(hostname)
    || /^100\.(6[4-9]|[7-9]\d|1[01]\d|12[0-7])\./.test(hostname)
    || /\.local$/.test(hostname);
}

function resolveCoreUrl() {
  const envUrl = sanitizeUrl(import.meta.env.VITE_DAILEY_CORE_URL
    || import.meta.env.VITE_CORE_URL);

  let derived = '';
  if (typeof window !== 'undefined') {
    const host = window.location.hostname || '';
    const scheme = window.location.protocol === 'https:' ? 'https' : 'http';
    if (host === 'thephotoharbor.com' || host === 'www.thephotoharbor.com') {
      // The PhotoHarbor domain is production and has no matching core.* host.
      derived = 'https://core.dailey.cloud';
    } else if (host.startsWith('photos-')) {
      derived = `${scheme}://${host.replace(/^photos-/, 'core-')}`;
    } else if (host.startsWith('photos.')) {
      derived = `${scheme}://${host.replace(/^photos\./, 'core.')}`;
    } else if (host.endsWith('.dailey.cloud')) {
      // DOS-hosted deployments don't follow the photos-* naming (e.g.
      // dailey-photos-staging.dailey.cloud) — pick Core by environment instead
      // of falling through to the localhost dev default.
      const sub = host.slice(0, -'.dailey.cloud'.length);
      const isStaging = /(^|-)(stg|staging)(-|$)/.test(sub);
      derived = `${scheme}://${isStaging ? 'core-stg' : 'core'}.dailey.cloud`;
    }
  }

  if (envUrl) {
    if (import.meta.env.PROD && typeof window !== 'undefined') {
      const pageIsHttps = window.location.protocol === 'https:';
      const pageHost = window.location.hostname || '';
      try {
        const parsed = new URL(envUrl);
        // Staging/prod cloud pages must not target local core domains.
        if (pageHost.endsWith('.dailey.cloud') && parsed.hostname.endsWith('.local') && derived) {
          return derived;
        }
        if (pageIsHttps && parsed.protocol === 'http:' && isPrivateHost(parsed.hostname)) {
          return derived || 'http://localhost:3002';
        }
        if (pageIsHttps && parsed.protocol === 'http:' && !isPrivateHost(parsed.hostname)) {
          return envUrl.replace(/^http:/, 'https:');
        }
      } catch (_) {}
    }
    return envUrl;
  }

  return derived || 'http://localhost:3002';
}

const DEFAULT_CORE_URL = resolveCoreUrl();

class AppAuth {
  constructor(baseUrl = DEFAULT_CORE_URL, appName = 'Photo Harbor', appSlug = 'dailey-photos') {
    this.baseUrl = baseUrl;
    this.appName = appName;
    this.appSlug = appSlug;
    this.token = localStorage.getItem('auth_token');
    this.refreshToken = localStorage.getItem('refresh_token');
    this.sessionSyncedToken = null;
    this.sessionSyncPromise = null;
    this.registrationPromise = null;
    this.registrationEmail = null;
    this.pendingRegistrationEmail = null;
    this.pendingRegistrationToken = null;
    this.tenantContext = this.loadTenantContext();
    this.pendingInviteToken = this.loadPendingInviteToken();
  }

  loadTenantContext() {
    try {
      const raw = localStorage.getItem('tenant_context');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  setTenantContext(context) {
    updatePrivateCacheTenant(context?.id || context?.slug);
    this.tenantContext = context;
    if (context) {
      localStorage.setItem('tenant_context', JSON.stringify(context));
    } else {
      localStorage.removeItem('tenant_context');
    }
  }

  setToken(accessToken) {
    const changed = this.token !== accessToken;
    this.token = accessToken;
    if (accessToken) {
      localStorage.setItem('auth_token', accessToken);
    } else {
      localStorage.removeItem('auth_token');
    }
    if (changed) {
      this.sessionSyncedToken = null;
    }
  }

  setRefreshToken(refreshToken) {
    this.refreshToken = refreshToken;
    if (refreshToken) {
      localStorage.setItem('refresh_token', refreshToken);
    } else {
      localStorage.removeItem('refresh_token');
    }
  }

  getRefreshToken() {
    return this.refreshToken || localStorage.getItem('refresh_token') || null;
  }

  /**
   * Attempt to refresh the access token using the stored refresh token.
   * Returns the new access_token or null if refresh fails.
   */
  async tryRefreshToken() {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) return null;

    try {
      const response = await fetch(`${this.baseUrl}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Client-Id': this.appSlug,
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        if (response.status >= 500 || response.status === 429 || response.status === 408) {
          throw Object.assign(new Error('Authentication is temporarily unavailable. Retry to reconnect.'), { retryable: true });
        }
        console.warn('[AppAuth] Token refresh failed:', response.status);
        this.setRefreshToken(null);
        return null;
      }

      const data = await response.json();
      if (data.access_token) {
        this.setToken(data.access_token);
        if (data.refresh_token) this.setRefreshToken(data.refresh_token);
        await this.syncSessionWithBackend();
        console.log('[AppAuth] Token refreshed successfully');
        return data.access_token;
      }
      return null;
    } catch (err) {
      throw Object.assign(err, { retryable: err.retryable ?? true });
    }
  }

  loadPendingInviteToken() {
    return localStorage.getItem('pending_invite_token');
  }

  getPendingInviteToken() {
    return this.pendingInviteToken || null;
  }

  setPendingInviteToken(token) {
    this.pendingInviteToken = token;
    if (token) {
      localStorage.setItem('pending_invite_token', token);
    } else {
      localStorage.removeItem('pending_invite_token');
    }
  }

  clearPendingInviteToken() {
    this.pendingInviteToken = null;
    localStorage.removeItem('pending_invite_token');
  }

  resolveTenantContext(data = {}) {
    if (!data) return null;
    if (data.tenant_id || data.tenant_slug) {
      return {
        id: data.tenant_id || null,
        slug: data.tenant_slug || null,
        name: data.tenant_name || null
      };
    }

    const tenants = Array.isArray(data.tenants) ? data.tenants : [];
    if (!tenants.length) return null;

    const defaultId = data.default_tenant_id || data.tenant_id || null;
    const defaultSlug = data.default_tenant_slug || data.tenant_slug || null;
    const preferred = tenants.find(t => (defaultId && (t.tenant_id || t.id) === defaultId) ||
      (defaultSlug && (t.slug || t.tenant_slug) === defaultSlug)) || tenants[0];

    return preferred ? {
      id: preferred.tenant_id || preferred.id || null,
      slug: preferred.slug || preferred.tenant_slug || null,
      name: preferred.name || null
    } : null;
  }

  applyTenantContext(data) {
    const context = this.resolveTenantContext(data);
    if (context) {
      this.setTenantContext(context);
    }
  }

  async syncSessionWithBackend() {
    if (!this.token) return;
    if (this.sessionSyncedToken === this.token) return;
    if (this.sessionSyncPromise) return this.sessionSyncPromise;

    const tokenToSync = this.token;
    this.sessionSyncPromise = (async () => {
      try {
        const response = await fetch('/api/auth/session', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${tokenToSync}`
          },
          credentials: 'include'
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw Object.assign(new Error(data.message || 'Your account is ready, but Photo Harbor could not finish signing you in. Retry to reconnect.'), { retryable: response.status >= 500 || response.status === 429, code: 'SESSION_SYNC_FAILED' });
        }

        if (this.token === tokenToSync) {
          this.sessionSyncedToken = tokenToSync;
        }
      } catch (error) {
        throw Object.assign(error, { retryable: error.retryable ?? true, code: error.code || 'SESSION_SYNC_FAILED' });
      } finally {
        this.sessionSyncPromise = null;
      }
    })();

    return this.sessionSyncPromise;
  }

  async login(email, password) {
    clearPrivateCacheScope();
    // Clear any stale tenant context before login - server will determine the right tenant
    this.setTenantContext(null);

    const response = await fetch(`${this.baseUrl}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // IMPORTANT: Add these headers for proper audit log context
        'X-Application': this.appName,
        'X-App-Name': this.appName,
        'X-Client-Id': this.appSlug
      },
      body: JSON.stringify({
        email,
        password,
        app_name: this.appName,
        app_slug: this.appSlug
        // Don't send tenant_slug - let server determine based on user's memberships
      })
    });

    const data = await response.json();
    if (data.access_token) {
      this.setToken(data.access_token);
      if (data.refresh_token) this.setRefreshToken(data.refresh_token);
      this.applyTenantContext(data);
      await this.syncSessionWithBackend();
      return data;
    }

    // Check if passkey is required (user has passkeys registered)
    if (data.code === 'PASSKEY_REQUIRED') {
      const error = new Error(data.message || 'Passkey required for enhanced security');
      error.code = 'PASSKEY_REQUIRED';
      error.email = email;
      throw error;
    }

    throw new Error(data.error || 'Login failed');
  }

  async forceChangePassword(newPassword) {
    if (!this.token) throw new Error('Authentication required');
    // Proxy through Photos backend to avoid CORS preflight issues with Core via Cloudflare
    const response = await fetch('/api/auth/force-change-password', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}`
      },
      body: JSON.stringify({ new_password: newPassword })
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to change password');
    }
    return data;
  }

  register(details) {
    const email = String(details.email || '').trim().toLowerCase();
    if (this.registrationPromise) {
      if (this.registrationEmail !== email) return Promise.reject(new Error('Another signup is in progress. Wait for it to finish.'));
      return this.registrationPromise;
    }
    this.registrationEmail = email;
    this.registrationPromise = this.completeRegistration({ ...details, email }).finally(() => {
      this.registrationPromise = null;
      this.registrationEmail = null;
    });
    return this.registrationPromise;
  }

  async completeRegistration({ email, password, name, plan }) {
    // Once Core may have created the account, retries must reconnect, not create it again.
    if (this.pendingRegistrationEmail === email) {
      if (!this.token || this.token !== this.pendingRegistrationToken) return this.login(email, password);
      await this.syncSessionWithBackend();
      return { access_token: this.token, resumed: true };
    }
    clearPrivateCacheScope();
    this.setTenantContext(null);
    this.setToken(null);
    this.setRefreshToken(null);
    this.pendingRegistrationToken = null;
    // Attempt to create an account in Dailey Core
    const payload = {
      email,
      password,
      name,
      display_name: name,
      plan,
      subscription_tier: plan,
      app_name: this.appName,
      app_slug: this.appSlug
    };
    let response;
    this.pendingRegistrationEmail = email;
    try {
      response = await fetch(`${this.baseUrl}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Application': this.appName,
          'X-App-Name': this.appName,
          'X-Client-Id': this.appSlug,
          ...(this.tenantContext?.slug ? { 'X-Tenant-Slug': this.tenantContext.slug } : {})
        },
        body: JSON.stringify(payload)
      });

    } catch {
      throw Object.assign(new Error('We could not confirm whether your account was created. Retry to sign in safely, or use Sign in if you reload this page.'), { retryable: true, code: 'SIGNUP_UNCERTAIN' });
    }
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      if (response.status < 500 && response.status !== 408) this.pendingRegistrationEmail = null;
      throw new Error(data.error || data.message || 'Sign up failed');
    }

    // If Core returns an access token on registration, use it
    if (data.access_token) {
      this.setToken(data.access_token);
      this.setRefreshToken(data.refresh_token || null);
      this.pendingRegistrationToken = data.access_token;
      this.applyTenantContext(data);
      await this.syncSessionWithBackend();
      return data;
    }

    // Otherwise, immediately login to obtain a token
    const loginData = await this.login(email, password);
    return loginData;
  }

  async bootstrapTenant({ name, tenantId, tenantSlug } = {}) {
    if (!this.token) throw new Error('Authentication required');
    const payload = JSON.stringify({
      name,
      tenant_id: tenantId,
      tenant_slug: tenantSlug
    });

    // Compatibility: some Core deployments removed or protected bootstrap routes.
    // Try both legacy and /api prefixed paths, then let caller fall back.
    const bootstrapUrls = [
      `${this.baseUrl}/apps/${this.appSlug}/bootstrap`,
      `${this.baseUrl}/api/apps/${this.appSlug}/bootstrap`
    ];

    let response = null;
    for (const url of bootstrapUrls) {
      let candidate = null;
      try {
        candidate = await this.makeAuthenticatedRequest(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: payload
        });
      } catch (error) {
        // Some Core deployments reject legacy bootstrap paths with 401.
        // Treat it as unavailable and continue to fallback behavior.
        if (/Authentication required/i.test(String(error?.message || ''))) {
          continue;
        }
        throw error;
      }
      if (candidate.status === 404 || candidate.status === 403 || candidate.status === 401) {
        continue;
      }
      response = candidate;
      break;
    }

    if (!response) {
      throw new Error('Tenant bootstrap endpoint not available');
    }

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.error || 'Failed to bootstrap tenant');
    }

    if (data.selected_access_token) {
      this.setToken(data.selected_access_token);
    }
    if (data.tenants && data.tenants.length > 0) {
      const preferred = data.tenants.find(t => t.tenant_id === data.default_tenant_id) || data.tenants[0];
      this.setTenantContext({
        id: preferred.tenant_id,
        slug: preferred.slug,
        name: preferred.name
      });
    }

    await this.syncSessionWithBackend();
    return data;
  }

  async ensureTenantScope({ name } = {}) {
    if (!this.token) return null;
    let bootstrap;
    try {
      bootstrap = await this.bootstrapTenant({ name });
    } catch (error) {
      // Modern Core path: no bootstrap endpoint required.
      if (!/bootstrap endpoint not available/i.test(String(error?.message || ''))) {
        throw error;
      }
      const validated = await this.validateToken();
      if (!this.tenantContext && validated) {
        this.applyTenantContext(validated);
      }
      return {
        fallback: true,
        tenant_context: this.tenantContext || null
      };
    }

    if (bootstrap?.needs_tenant_selection && Array.isArray(bootstrap.tenants) && bootstrap.tenants.length > 0) {
      const preferred = bootstrap.tenants.find(t => t.tenant_id === bootstrap.default_tenant_id) || bootstrap.tenants[0];
      if (preferred?.tenant_id || preferred?.slug) {
        await this.switchTenant({
          tenantId: preferred.tenant_id || null,
          tenantSlug: preferred.slug || null
        });
      }
    }
    return bootstrap;
  }

  async switchTenant({ tenantId, tenantSlug } = {}) {
    if (!this.token) throw new Error('Authentication required');
    const response = await this.makeAuthenticatedRequest(
      `${this.baseUrl}/auth/switch-tenant`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenant_id: tenantId,
          tenant_slug: tenantSlug,
          app_slug: this.appSlug
        })
      }
    );

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.error || 'Failed to switch tenant');
    }

    if (data.access_token) {
      this.setToken(data.access_token);
    }
    if (data.tenant_id || data.tenant_slug) {
      this.setTenantContext({
        id: data.tenant_id || tenantId || null,
        slug: data.tenant_slug || tenantSlug || null,
        name: data.tenant_name || null
      });
    }

    await this.syncSessionWithBackend();
    return data;
  }

  async acceptInvite(token) {
    const inviteToken = token || this.pendingInviteToken;
    if (!inviteToken) {
      throw new Error('Invite token required');
    }

    const response = await fetch(`${this.baseUrl}/invites/accept`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(this.token ? { 'Authorization': `Bearer ${this.token}` } : {})
      },
      body: JSON.stringify({ token: inviteToken })
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.error || 'Failed to accept invite');
    }

    if (data.needs_signup) {
      this.setPendingInviteToken(inviteToken);
      return data;
    }

    this.clearPendingInviteToken();

    if (data.access_token) {
      this.setToken(data.access_token);
    }

    if (data.access_token) {
      await this.validateToken();
    } else {
      await this.ensureTenantScope();
    }

    return data;
  }

  async validateToken() {
    if (!this.token) return null;

    let response;
    try {
      response = await fetch(`${this.baseUrl}/auth/me`, {
        headers: { 'Authorization': `Bearer ${this.token}` }
      });
    } catch {
      throw Object.assign(new Error('Authentication is temporarily unavailable. Retry to reconnect.'), { retryable: true });
    }
    if (response.status >= 500 || response.status === 429 || response.status === 408) {
      throw Object.assign(new Error('Authentication is temporarily unavailable. Retry to reconnect.'), { retryable: true });
    }

    if (response.ok) {
      const profile = await response.json();
      const claims = parseJwtPayload(this.token) || {};
      const roles = Array.isArray(claims.roles)
        ? claims.roles
        : (claims.roles ? [claims.roles] : []);
      const tenants = Array.isArray(claims.tenants) && claims.tenants.length > 0
        ? claims.tenants
        : ((claims.tenant_id || claims.tenant || claims.tenant_slug)
          ? [{
              tenant_id: claims.tenant_id || claims.tenant || null,
              tenant_slug: claims.tenant_slug || null,
              id: claims.tenant_id || claims.tenant || null,
              slug: claims.tenant_slug || null,
              status: 'active'
            }]
          : []);
      const result = {
        valid: true,
        user: {
          ...(profile.user || {}),
          tenant_id: claims.tenant_id || claims.tenant || null,
          tenant_slug: claims.tenant_slug || null,
          app_id: claims.app_id || null,
          app_slug: claims.app_slug || this.appSlug
        },
        roles,
        tenants,
        token_info: {
          ...(profile.token_info || {}),
          roles,
          app_id: profile.token_info?.app_id || claims.app_id || null
        }
      };
      this.applyTenantContext(result);
      await this.syncSessionWithBackend();
      return result;
    }
    
    // Token invalid, clear it
    this.logout();
    return null;
  }

  logout() {
    clearPrivateCacheScope();
    this.pendingRegistrationEmail = null;
    this.pendingRegistrationToken = null;
    this.setToken(null);
    this.setRefreshToken(null);
    this.setTenantContext(null);
    this.sessionSyncPromise = null;
  }

  isAuthenticated() {
    return !!this.token;
  }

  getToken() {
    return this.token;
  }

  async makeAuthenticatedRequest(url, options = {}) {
    const headers = {
      ...options.headers,
      'Authorization': `Bearer ${this.token}`,
      'X-Application': this.appName,
      'X-App-Name': this.appName,
      'X-Client-Id': this.appSlug,
      ...(this.tenantContext?.slug ? { 'X-Tenant-Slug': this.tenantContext.slug } : {}),
      ...(this.tenantContext?.id ? { 'X-Tenant-Id': this.tenantContext.id } : {})
    };

    const response = await fetch(url, {
      ...options,
      headers
    });

    // If 401, try to refresh or logout
    if (response.status === 401) {
      this.logout();
      throw new Error('Authentication required');
    }

    return response;
  }

  // WebAuthn (Passkey) Methods
  
  /**
   * Register a new passkey for the current user
   */
  async registerPasskey() {
    if (!this.token) {
      throw new Error('Must be logged in to register a passkey');
    }

    // Start registration flow
    const optionsResponse = await this.makeAuthenticatedRequest(
      `${this.baseUrl}/auth/webauthn/register/start`,
      { method: 'POST' }
    );
    
    if (!optionsResponse.ok) {
      throw new Error('Failed to start passkey registration');
    }
    
    const options = await optionsResponse.json();
    
    // Use browser WebAuthn API to create credential
    // Note: @simplewebauthn/browser v10+ requires { optionsJSON: options } format
    const credential = await startRegistration({ optionsJSON: options });
    
    // Finish registration
    const finishResponse = await this.makeAuthenticatedRequest(
      `${this.baseUrl}/auth/webauthn/register/finish`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credential)
      }
    );
    
    if (!finishResponse.ok) {
      throw new Error('Failed to complete passkey registration');
    }
    
    return await finishResponse.json();
  }

  /**
   * Authenticate using a passkey
   */
  async loginWithPasskey(email) {
    // Start authentication flow
    const optionsResponse = await fetch(`${this.baseUrl}/auth/webauthn/login/start`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'X-Application': this.appName,
        'X-App-Name': this.appName,
        'X-Client-Id': this.appSlug
      },
      body: JSON.stringify({ email })
    });
    
    if (!optionsResponse.ok) {
      throw new Error('Failed to start passkey authentication');
    }
    
    const options = await optionsResponse.json();

    // Use browser WebAuthn API to get assertion
    // Note: @simplewebauthn/browser v10+ requires { optionsJSON: options } format
    const credential = await startAuthentication({ optionsJSON: options });

    // Finish authentication - must include email in the body
    const finishResponse = await fetch(`${this.baseUrl}/auth/webauthn/login/finish`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Application': this.appName,
        'X-App-Name': this.appName
      },
      body: JSON.stringify({ ...credential, email })
    });
    
    if (!finishResponse.ok) {
      throw new Error('Failed to complete passkey authentication');
    }
    
    const data = await finishResponse.json();
    
    if (data.access_token) {
      this.token = data.access_token;
      localStorage.setItem('auth_token', this.token);
      this.applyTenantContext(data);
      await this.syncSessionWithBackend();
      return data;
    }
    
    throw new Error('Passkey authentication failed');
  }

  /**
   * Check if the browser supports WebAuthn
   */
  isWebAuthnSupported() {
    return this.getWebAuthnSupport().available;
  }

  getWebAuthnSupport() {
    if (typeof window === 'undefined') {
      return { available: false, hasApi: false, isSecureContext: false };
    }
    const hasApi = typeof window.PublicKeyCredential !== 'undefined';
    const isSecureContext = window.isSecureContext;
    return {
      available: hasApi && isSecureContext,
      hasApi,
      isSecureContext
    };
  }

  /**
   * Check if the user has any passkeys registered
   */
  async hasPasskeys() {
    if (!this.token) return false;

    try {
      const response = await this.makeAuthenticatedRequest(
        `${this.baseUrl}/auth/webauthn/credentials`
      );

      if (response.ok) {
        const data = await response.json();
        return data.credentials && data.credentials.length > 0;
      }
    } catch (error) {
      console.warn('Failed to check passkeys:', error);
    }

    return false;
  }

  /**
   * Get list of registered passkeys for the current user
   */
  async getPasskeys() {
    if (!this.token) return [];

    try {
      const response = await this.makeAuthenticatedRequest(
        `${this.baseUrl}/auth/webauthn/credentials`
      );

      if (response.ok) {
        const data = await response.json();
        return data.credentials || [];
      }
    } catch (error) {
      console.warn('Failed to get passkeys:', error);
    }

    return [];
  }

  /**
   * Delete a registered passkey by ID
   */
  async deletePasskey(credentialId) {
    if (!this.token) throw new Error('Authentication required');

    const response = await this.makeAuthenticatedRequest(
      `${this.baseUrl}/auth/webauthn/credentials/${credentialId}`,
      { method: 'DELETE' }
    );

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || 'Failed to delete passkey');
    }

    return true;
  }
}

export default AppAuth;
